import { NON_PHOTO_LAYOUTS } from '@shared/utils/websiteDesign';
import { isPhotoTombstoned } from '@shared/utils/eventPhoto/isPhotoTombstoned';
import { isAnyDefaultPhotoUrl } from '@shared/utils/eventPhoto/isAnyDefaultPhotoUrl';
import type { SupportedHeaderPhotoLayoutSchema } from '@shared/types/WebsiteHeaderSettings.types';
import type { EventWebsiteDesignQuery } from './useEventWebsiteDesign.graphql.generated';
import type { EventWebsiteDesign, EventWebsiteDesignColors, EventWebsiteHeroPhoto } from './useEventWebsiteDesign.types';

type DesignEvent = NonNullable<EventWebsiteDesignQuery['eventByName']>;
type V1Design = NonNullable<DesignEvent['eventDesign']>;
type V2Site = NonNullable<DesignEvent['activeWebsiteV2Site']>;

export const EMPTY: EventWebsiteDesign = { loading: false, themeId: null, layoutType: null, colors: null, titleFont: null, heroPhotos: [] };
export const LOADING: EventWebsiteDesign = { ...EMPTY, loading: true };

const toLayoutType = (layoutType: string | null | undefined): EventWebsiteDesign['layoutType'] => (layoutType === 'aloha' || layoutType === 'brannan' ? layoutType : null);

// V1 stores `'blank'` for "no sprite theme"; the shared shape uses null (as the V2 field does).
const toThemeId = (themeId: string | null | undefined): string | null => (themeId && themeId !== 'blank' ? themeId : null);

// Missing V1 colors take a sibling's value, as the V2 adapter does; without page colors there is
// nothing to tint with.
const toV1Colors = (design: V1Design): EventWebsiteDesignColors | null => {
  const { pageBackgroundColor, pageFontColor } = design;
  if (!pageBackgroundColor || !pageFontColor) return null;
  return {
    pageBackground: pageBackgroundColor,
    pageFont: pageFontColor,
    artBackground: design.artBackgroundColor || pageBackgroundColor,
    artFont: design.artFontColor || pageFontColor,
    linkAndButton: design.linkAndButtonColor || pageFontColor,
  };
};

// A photo-less header layout can still carry photos in its data; it shows none.
const toV1HeroPhotos = (design: V1Design): ReadonlyArray<EventWebsiteHeroPhoto> => {
  const layout = design.activeWebsiteHeaderPresentationLayout?.dataJSON as SupportedHeaderPhotoLayoutSchema | undefined;
  if (layout?.presentationLayout && NON_PHOTO_LAYOUTS.has(layout.presentationLayout)) return [];
  const photos: ReadonlyArray<{ url: string; deleted?: boolean }> = (layout && 'photos' in layout && layout.photos) || [];
  return photos.filter(photo => !isPhotoTombstoned(photo)).map(photo => ({ url: photo.url, placeholder: isAnyDefaultPhotoUrl(photo.url) }));
};

export const fromV1 = (design: V1Design): EventWebsiteDesign => ({
  loading: false,
  themeId: toThemeId(design.theme.themeId),
  layoutType: toLayoutType(design.websiteLayout?.layoutType),
  colors: toV1Colors(design),
  titleFont: design.fonts?.find(f => f.category === 'EVENT_TITLE')?.font?.fontFamily ?? design.font?.fontFamily ?? null,
  heroPhotos: toV1HeroPhotos(design),
});

export const fromV2 = (site: V2Site): EventWebsiteDesign => ({
  loading: false,
  themeId: toThemeId(site.legacyDesign?.themeId),
  layoutType: toLayoutType(site.legacyDesign?.layoutType),
  colors: site.legacyDesign?.colors ?? null,
  titleFont: site.legacyDesign?.titleFont ?? null,
  heroPhotos: site.heroPhotos.flatMap(photo => (photo.url ? [{ url: photo.url, placeholder: photo.placeholder }] : [])),
});

/**
 * A Website V2 event reads its site (the service returns it only for V2 events, and picks the one
 * withjoy.com/:handle serves); every other event reads the live charm design. A V2 event never
 * falls back to the charm record: for it that is the retired design.
 */
export const fromEvent = (event: DesignEvent | null | undefined): EventWebsiteDesign => {
  if (event?.activeWebsiteV2Site) return fromV2(event.activeWebsiteV2Site);
  return event?.eventDesign ? fromV1(event.eventDesign) : EMPTY;
};
